import API_BASE_URL from '@/config/apiConfig';
import {
  CompanySettings,
  LogoType,
  PatchCompanySettingsRequest,
  PutSettingsAccessRequest,
  SettingsAccessConfig,
  SettingsClientCache,
  SettingsVersionResponse,
} from '../types/settings';
import { applyDocumentBranding } from '../utils/settingsBranding';
import { saveSettingsClientCache } from '../utils/settingsSecureStorage';
import { apiService } from './api';

export function getSettingsLogoUrl(imagePath: string): string {
  if (!imagePath?.trim()) return '';
  if (imagePath.startsWith('http')) return imagePath;
  const origin = API_BASE_URL.replace(/\/api\/v\d+\/?$/i, '');
  const normalized = imagePath.replace(/^\/+/, '');
  const path = normalized.startsWith('upload/') ? normalized : `upload/${normalized}`;
  return `${origin}/${path}`;
}

function unwrap<T>(response: unknown): T {
  const r = response as { data?: { data?: T; success?: boolean } & T };
  if (r?.data && typeof r.data === 'object' && 'data' in r.data) {
    return (r.data as { data: T }).data;
  }
  if (r?.data) {
    return r.data as T;
  }
  return response as T;
}

function accessFromSettings(settings: CompanySettings): SettingsAccessConfig {
  return {
    versionId: settings.versionId,
    logos: settings.logos ?? {},
    tradingName: settings.companyDetails?.tradingName,
    legalName: settings.companyDetails?.legalName,
    penaltyFee: settings.penaltyFee,
    isZeettuEnabled: settings.isZeettuEnabled !== false,
    isInstallmentEnabled: settings.isInstallmentEnabled !== false,
    isPOSEnabled: settings.isPOSEnabled !== false,
    isBDenabled: settings.isBDenabled !== false,
    posBlindShiftClose: settings.posBlindShiftClose !== false,
    posIdleLockMinutes: settings.posIdleLockMinutes,
    posVarianceNoteThreshold: settings.posVarianceNoteThreshold,
    artificialDelay: settings.artificialDelay,
    routeAccessRules: settings.routeAccessRules ?? [],
    adminOnlyPrefixes: settings.adminOnlyPrefixes ?? [],
    rolePermissions: settings.rolePermissions ?? [],
  };
}

export function buildClientCacheFromSettings(settings: CompanySettings): SettingsClientCache | null {
  if (!settings.versionId) return null;
  const logos = settings.logos ?? {};
  return {
    versionId: settings.versionId,
    access: accessFromSettings(settings),
    logos,
    companyDetails: settings.companyDetails,
    legalPolicies: settings.legalPolicies,
    penaltyFee: settings.penaltyFee,
  };
}

export const settingsService = {
  async getSettingsVersion(): Promise<SettingsVersionResponse> {
    const response = await apiService.get<unknown>('/settings/version');
    return unwrap<SettingsVersionResponse>(response);
  },

  async getSettings(): Promise<CompanySettings> {
    const response = await apiService.get<unknown>('/settings');
    return unwrap<CompanySettings>(response);
  },

  async patchSettings(body: PatchCompanySettingsRequest): Promise<CompanySettings> {
    const response = await apiService.patch<unknown>('/settings', body);
    const updated = unwrap<CompanySettings>(response);
    await settingsService.persistClientCacheFromSettings(updated);
    return updated;
  },

  async getAccessConfig(): Promise<SettingsAccessConfig> {
    const response = await apiService.get<unknown>('/settings/access');
    return unwrap<SettingsAccessConfig>(response);
  },

  async putAccessConfig(body: PutSettingsAccessRequest): Promise<CompanySettings> {
    const response = await apiService.put<unknown>('/settings/access', body);
    const updated = unwrap<CompanySettings>(response);
    await settingsService.persistClientCacheFromSettings(updated);
    return updated;
  },

  async uploadLogo(type: LogoType, file: File): Promise<CompanySettings> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiService.post<unknown>(`/settings/logos/${type}`, formData);
    const updated = unwrap<CompanySettings>(response);
    await settingsService.persistClientCacheFromSettings(updated);
    return updated;
  },

  async deleteLogo(type: LogoType): Promise<CompanySettings> {
    const response = await apiService.delete<unknown>(`/settings/logos/${type}`);
    const updated = unwrap<CompanySettings>(response);
    await settingsService.persistClientCacheFromSettings(updated);
    return updated;
  },

  async persistClientCacheFromSettings(settings: CompanySettings): Promise<void> {
    const cache = buildClientCacheFromSettings(settings);
    if (!cache) return;
    await saveSettingsClientCache(cache);
    const title =
      settings.companyDetails?.tradingName?.trim() ||
      settings.companyDetails?.legalName?.trim();
    applyDocumentBranding(settings.logos ?? {}, title);
  },

  async persistClientCache(cache: SettingsClientCache): Promise<void> {
    await saveSettingsClientCache(cache);
  },

  /** @deprecated Use encrypted cache via settingsSecureStorage */
  clearAccessCache(): void {
    sessionStorage.removeItem('zeettu_settings_access_v1');
  },
};
